import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { QrImage, TransferPlanQr, useQrSvg } from '@/components/equipment/storage/replace/TransferPlanQr'
import { itemCode, itemDetail, itemKindLabel } from '@/components/equipment/storage/replace/movePreview'
import { STATUS_WORD } from '@/components/equipment/storage/replace/TransferStatusChip'
import { transferMoveUrl } from '@/lib/transferQr'
import {
  moveStatusText,
  sheetMoveGroups,
  showPerMoveQr,
  tooManyMovesForQrNote,
  transferPrintTitle,
} from '@/lib/transferPrint'
import type { StorageTransferDetail, StorageTransferMove } from '@/types/storage'

/**
 * The plan sheet a tech carries to the freezer (user request, Task 11).
 *
 * The plan's own QR up top — same `TransferPlanQr` the screen shows, so the
 * "Scan to open this plan on a handset" code is identical on paper and on
 * screen — then every move grouped by source box exactly as `MovePreviewTable`
 * shows it (`sheetMoveGroups` wraps the same `groupMovesByContainer` the table
 * uses), with a small QR per row for a tech who would rather scan than read.
 *
 * Print rules live with the sheet, the same way `PoDocument`'s do: the table
 * header repeats on every printed page (`display: table-header-group`) and a
 * row never splits across one (`break-inside: avoid`). Rendered inside
 * `StorageTransferPrintPage`, which is outside `Layout` so no app chrome has
 * to be hidden from the printer.
 *
 * Above `PER_MOVE_QR_LIMIT` moves, no per-row QR is drawn at all (fix round
 * 1): the Scan column is dropped and a note under the plan QR points at it
 * instead, for the same reason `TransferMoveQrDialog` never draws one per row
 * on screen. Below the cap, `onReadyChange` reports once every row's QR has
 * settled (drawn or failed), so the print toolbar can hold the Print button
 * until nothing on the sheet is still half-drawn.
 */
export function TransferPrintSheet({
  transfer,
  sourceCode,
  targetCode,
  onReadyChange,
}: {
  transfer: StorageTransferDetail
  sourceCode: string
  targetCode: string
  /** Called whenever "every per-move QR has settled" changes; true immediately when there's nothing to wait for. */
  onReadyChange?: (ready: boolean) => void
}) {
  const groups = sheetMoveGroups(transfer.moves)
  const printedAt = new Date()
  const moveCount = transfer.moves.length
  const perMoveQr = showPerMoveQr(moveCount)

  const [settledIds, setSettledIds] = useState<ReadonlySet<string>>(() => new Set())
  const onRowSettled = useCallback((moveId: string) => {
    setSettledIds((prev) => (prev.has(moveId) ? prev : new Set(prev).add(moveId)))
  }, [])

  const ready = !perMoveQr || settledIds.size >= moveCount
  useEffect(() => {
    onReadyChange?.(ready)
  }, [ready, onReadyChange])

  return (
    <div className='transfer-print-sheet text-slate-900'>
      <style>{`
        @media print {
          .transfer-print-sheet thead { display: table-header-group; }
          .transfer-print-sheet tr, .transfer-print-sheet .print-keep { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <header className='print-keep mb-6 border-b pb-4'>
        <h1 className='text-xl font-semibold'>{transferPrintTitle(sourceCode, targetCode)}</h1>
        <p className='mt-1 text-sm capitalize text-slate-600'>
          {STATUS_WORD[transfer.status]}
          {transfer.reason && ` · ${transfer.reason.label}`}
          {transfer.reasonNote && ` — ${transfer.reasonNote}`}
        </p>
        <p className='mt-0.5 text-xs text-slate-500'>Printed {printedAt.toLocaleString()}</p>
      </header>

      {/* Same rule as the on-screen page: only an approved plan gets a code,
          since a draft can still change under whoever walks off with it. */}
      {(transfer.status === 'READY' || transfer.status === 'IN_PROGRESS') && (
        <div className='print-keep mb-6'>
          <TransferPlanQr transferId={transfer.id} />
          {!perMoveQr && (
            <p className='mt-2 max-w-md text-xs text-slate-600'>{tooManyMovesForQrNote(moveCount)}</p>
          )}
        </div>
      )}

      <div className='space-y-6'>
        {groups.map((group) => (
          <section key={group.container} className='print-keep'>
            <h2 className='mb-2 font-mono text-sm font-medium'>{group.container}</h2>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='border-b text-left text-[10px] uppercase tracking-wide text-slate-500'>
                  <th className='py-1.5 pr-3 font-medium'>Item</th>
                  <th className='py-1.5 pr-3 font-medium'>From</th>
                  <th className='py-1.5 pr-3 font-medium'>To</th>
                  <th className='py-1.5 pr-3 font-medium'>Status</th>
                  {perMoveQr && <th className='py-1.5 font-medium'>Scan</th>}
                </tr>
              </thead>
              <tbody>
                {group.moves.map((move) =>
                  perMoveQr ? (
                    <MoveSheetRowWithQr
                      key={move.id}
                      transferId={transfer.id}
                      move={move}
                      onSettled={onRowSettled}
                    />
                  ) : (
                    <MoveSheetRowCells key={move.id} move={move} />
                  ),
                )}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  )
}

/** The Item/From/To/Status cells, shared by both row shapes. */
function MoveSheetRowCells({ move, children }: { move: StorageTransferMove; children?: ReactNode }) {
  const kind = itemKindLabel(move.item)
  const detail = itemDetail(move.item)

  return (
    <tr className='border-b border-slate-200'>
      <td className='max-w-[18rem] py-1.5 pr-3 align-top'>
        <div className='font-mono text-[11px] font-medium'>{itemCode(move.item)}</div>
        {(kind || detail) && (
          <div className='text-[10px] text-slate-500'>
            {kind}
            {kind && detail && ' · '}
            {detail}
          </div>
        )}
      </td>
      <td className='whitespace-nowrap py-1.5 pr-3 align-top font-mono text-[11px]'>{move.sourceNodePath}</td>
      <td className='whitespace-nowrap py-1.5 pr-3 align-top font-mono text-[11px]'>
        {move.targetNodePath ?? '—'}
      </td>
      <td className='py-1.5 pr-3 align-top text-[11px]'>{moveStatusText(move)}</td>
      {children}
    </tr>
  )
}

/**
 * A row with its own Scan cell — only mounted below `PER_MOVE_QR_LIMIT`
 * moves, since `useQrSvg` here is one `QRCode.toString` call per row.
 * Reports to the sheet once this move's code has settled (drawn or failed),
 * so the print toolbar never offers to print a row still mid-draw.
 */
function MoveSheetRowWithQr({
  transferId,
  move,
  onSettled,
}: {
  transferId: string
  move: StorageTransferMove
  onSettled: (moveId: string) => void
}) {
  // Same-origin, as every QR here is: a baked-in host would send a staging
  // code at production.
  const url = transferMoveUrl(window.location.origin, transferId, move.id)
  const { svg, error } = useQrSvg(url)

  useEffect(() => {
    if (svg || error) onSettled(move.id)
  }, [svg, error, move.id, onSettled])

  return (
    <MoveSheetRowCells move={move}>
      <td className='py-1.5 align-top'>
        <QrImage svg={svg} label={`QR code for ${itemCode(move.item)}`} className='h-10 w-10' />
      </td>
    </MoveSheetRowCells>
  )
}
